/**
 * Guided duplicate merge (TRUS-558 merge v2).
 *
 * Opened from a duplicate group once the reviewer has picked the record to keep
 * (step 1, on the group card). The wizard then walks:
 *
 *   2. Fields — every field any record holds; where the records hold different real
 *      values the reviewer picks the winner (default: the record being kept).
 *      Placeholders ("n/a", "unknown", …) are shown greyed and count as blank.
 *   3. Cases  — each duplicate case (program enrollment). A case in a program the
 *      kept record has no case in moves with the person; a case in a program it
 *      already has one in needs an explicit Combine / Don't merge. A person can
 *      have only one case per program, so "Don't merge" on any case means the
 *      profiles are not merged at all: the final action becomes "Keep both
 *      profiles", which dismisses the group (POST /duplicates/dismiss).
 *   4. Confirm — what will happen, and that the merge can be reverted.
 *
 * The merge button stays disabled until every case that needs a decision has one;
 * the server refuses an undecided merge anyway (400 case_decisions_required).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowLeft, ArrowRight, Check, Info, Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  membersAPI,
  type CaseDecision,
  type DuplicateMergePreview,
  type DuplicateMergeResult,
  type MergeCase,
  type MergePreviewField,
} from '@/services/api/members';
import {
  DEFAULT_PLACEHOLDER_VALUES,
  buildCaseDecisions,
  buildFieldChoices,
  canSubmitMerge,
  caseDecisionState,
  defaultWinner,
  fieldOptions,
  mergedValueFor,
  mergeOutcome,
} from './mergeFlow';

type Step = 'fields' | 'cases' | 'confirm';

interface MergeWizardProps {
  survivorId: number;
  duplicateIds: number[];
  matchReason?: string;
  onCancel: () => void;
  onMerged: (result: DuplicateMergeResult) => void;
  /** "Don't merge" was chosen for a case: the group was dismissed, both profiles kept. */
  onKeptBoth: () => void;
}

const ONE_CASE_PER_PROGRAM =
  'A person can have only one case per program. If these are genuinely separate cases, keep both profiles instead of merging.';

function display(v: unknown): string {
  if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) return '—';
  if (typeof v === 'boolean') return v ? '✓' : '✕';
  return String(v);
}

const StepPill: React.FC<{ n: number; label: string; active: boolean; done: boolean }> = ({
  n,
  label,
  active,
  done,
}) => (
  <div
    className={`flex items-center gap-1.5 text-xs ${
      active ? 'font-semibold text-foreground' : 'text-muted-foreground'
    }`}
  >
    <span
      className={`inline-flex h-5 w-5 items-center justify-center rounded-full border text-[10px] ${
        done ? 'bg-primary text-primary-foreground border-primary' : active ? 'border-primary' : ''
      }`}
    >
      {done ? <Check className="h-3 w-3" /> : n}
    </span>
    {label}
  </div>
);

const MergeWizard: React.FC<MergeWizardProps> = ({
  survivorId,
  duplicateIds,
  matchReason,
  onCancel,
  onMerged,
  onKeptBoth,
}) => {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<DuplicateMergePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('fields');
  const [selection, setSelection] = useState<Record<string, number>>({});
  const [decisions, setDecisions] = useState<Record<string, CaseDecision>>({});
  const [showAll, setShowAll] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const dupKey = duplicateIds.join(',');
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelection({});
    setDecisions({});
    setStep('fields');
    membersAPI
      .previewDuplicateMerge(survivorId, duplicateIds)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((e: any) => {
        if (!cancelled)
          setError(
            e?.response?.data?.detail ||
              t('members.duplicates.wizard.previewFailed', 'Could not load the merge preview.')
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // duplicateIds is compared by value through dupKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survivorId, dupKey, t]);

  const placeholders = preview?.placeholder_values ?? DEFAULT_PLACEHOLDER_VALUES;
  const memberIds = useMemo(() => [survivorId, ...duplicateIds], [survivorId, duplicateIds]);
  const caseState = caseDecisionState(preview?.cases, decisions);
  const fieldChoices = preview ? buildFieldChoices(preview.fields, survivorId, selection) : {};
  const mergeReady = !!preview && canSubmitMerge(preview.cases, decisions);
  const outcome = mergeOutcome(preview?.cases, decisions);

  const nameOf = (id: number) => {
    const m = preview?.members[String(id)];
    const name = m ? [m.first_name, m.last_name].filter(Boolean).join(' ') : '';
    return `${name || t('members.duplicates.memberFallback', 'Member')} #${id}`;
  };

  const handleKeepBoth = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await membersAPI.dismissDuplicateMembers({
        member_ids: memberIds,
        match_reason: matchReason,
        notes: 'Kept both profiles: same-program cases marked "Don\'t merge" in the merge wizard.',
      });
      onKeptBoth();
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ||
          t('members.duplicates.wizard.keepBothFailed', 'Could not record that these profiles are kept.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleMerge = async () => {
    if (!preview || !mergeReady) return;
    if (outcome === 'keep_both') {
      await handleKeepBoth();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await membersAPI.mergeDuplicateMembers({
        survivor_id: survivorId,
        duplicate_ids: duplicateIds,
        match_reason: matchReason,
        field_choices: fieldChoices,
        case_decisions: buildCaseDecisions(preview.cases, decisions),
      });
      onMerged(result);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'merge_declined_cases') {
        setStep('cases');
        setError(t('members.duplicates.wizard.oneCasePerProgram', ONE_CASE_PER_PROGRAM));
      } else if (detail && typeof detail === 'object' && detail.error === 'case_decisions_required') {
        setStep('cases');
        setError(
          t(
            'members.duplicates.wizard.casesStillUndecided',
            'Some cases still need a decision before these records can be merged.'
          )
        );
      } else {
        setError(
          (typeof detail === 'string' && detail) ||
            (detail && typeof detail === 'object' && detail.error) ||
            t('members.duplicates.wizard.mergeFailed', 'Merge failed.')
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fieldRows = (preview?.fields ?? []).filter(
    (f) =>
      showAll ||
      fieldOptions(f, survivorId, placeholders).some((o) => !o.blank) ||
      !!f.choosable
  );

  const renderFields = () => (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b">
        <p className="text-xs text-muted-foreground">
          {t(
            'members.duplicates.wizard.fieldsHelp',
            'Where the records disagree, choose which value the merged profile keeps. Values like “n/a” or “unknown” count as blank and are filled from another record.'
          )}
        </p>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
          <input type="checkbox" checked={showAll} onChange={() => setShowAll((v) => !v)} />
          {t('members.duplicates.showEmptyFields', 'Show empty fields')}
        </label>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground border-b">
              <th className="px-3 py-2 font-medium">{t('members.duplicates.colField', 'Field')}</th>
              {memberIds.map((id) => (
                <th key={id} className="px-3 py-2 font-medium">
                  {id === survivorId
                    ? t('members.duplicates.colKeep', { name: nameOf(id), defaultValue: 'Keep: {{name}}' })
                    : t('members.duplicates.colDuplicate', {
                        name: nameOf(id),
                        defaultValue: 'Duplicate: {{name}}',
                      })}
                </th>
              ))}
              <th className="px-3 py-2 font-medium">{t('members.duplicates.colMerged', 'Merged result')}</th>
            </tr>
          </thead>
          <tbody>
            {fieldRows.length === 0 && (
              <tr>
                <td className="px-3 py-3 text-muted-foreground" colSpan={memberIds.length + 2}>
                  {t('members.duplicates.noValues', 'None of these records has a value in any field.')}
                </td>
              </tr>
            )}
            {fieldRows.map((f: MergePreviewField) => {
              const options = fieldOptions(f, survivorId, placeholders);
              const winner = selection[f.field] ?? defaultWinner(f, survivorId);
              const merged = mergedValueFor(f, survivorId, selection[f.field]);
              return (
                <tr
                  key={f.field}
                  className={`border-b last:border-0 ${f.choosable ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}
                >
                  <td className="px-3 py-1.5 font-medium align-top">
                    {f.label}
                    {f.choosable && (
                      <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] text-amber-700 dark:text-amber-300">
                        <AlertTriangle className="w-3 h-3" />
                        {t('members.duplicates.differs', 'differs')}
                      </span>
                    )}
                  </td>
                  {options.map((o) => (
                    <td key={o.memberId} className="px-3 py-1.5 align-top break-words">
                      {f.choosable && !o.blank ? (
                        <label className="flex items-start gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            className="mt-0.5"
                            name={`field-${f.field}`}
                            checked={winner === o.memberId}
                            onChange={() => setSelection((s) => ({ ...s, [f.field]: o.memberId }))}
                          />
                          <span>{display(o.value)}</span>
                        </label>
                      ) : (
                        <span
                          className={o.blank ? 'text-muted-foreground/60 italic' : ''}
                          title={
                            o.blank && !(o.value === null || o.value === undefined || o.value === '')
                              ? t('members.duplicates.wizard.placeholderTitle', 'Counts as blank')
                              : undefined
                          }
                        >
                          {display(o.value)}
                        </span>
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-1.5 align-top break-words">
                    <div className="font-medium">{display(merged)}</div>
                    {f.source === 'not_merged' && (
                      <div className="text-[10px] text-amber-700 dark:text-amber-300">
                        {t('members.duplicates.notCopied', 'not copied by a merge')}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const caseCounts = (c: MergeCase) =>
    t('members.duplicates.wizard.caseCounts', {
      notes: c.notes,
      docs: c.docs,
      forms: c.forms,
      defaultValue: '{{notes}} notes · {{docs}} documents · {{forms}} forms',
    });

  const renderCases = () => (
    <div className="p-3 space-y-3">
      {caseState.auto.length === 0 && caseState.required.length === 0 && (
        <p className="text-xs text-muted-foreground">
          {t('members.duplicates.wizard.noCases', 'The duplicate records have no cases.')}
        </p>
      )}
      {caseState.auto.length > 0 && (
        <div className="rounded border border-dashed p-2">
          <div className="flex items-center gap-1.5 text-xs font-medium mb-1">
            <Info className="w-3.5 h-3.5" />
            {t('members.duplicates.wizard.autoCasesTitle', 'These cases move to the kept record as they are')}
          </div>
          <ul className="text-xs text-muted-foreground space-y-0.5">
            {caseState.auto.map((c) => (
              <li key={c.enrollment_id}>
                {t('members.duplicates.wizard.caseLine', {
                  program: c.program_name,
                  id: c.enrollment_id,
                  status: c.status,
                  defaultValue: '{{program}} (case {{id}}, {{status}})',
                })}
                {' — '}
                {caseCounts(c)}
              </li>
            ))}
          </ul>
        </div>
      )}
      {caseState.required.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {t(
              'members.duplicates.wizard.requiredCasesHelp',
              'The kept record already has a case in these programs. Choose what happens to each duplicate case.'
            )}
          </p>
          <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            {t('members.duplicates.wizard.oneCasePerProgram', ONE_CASE_PER_PROGRAM)}
          </p>
          {caseState.required.map((c) => {
            const key = String(c.enrollment_id);
            const current = decisions[key];
            return (
              <div
                key={key}
                className={`rounded border p-2 ${current ? '' : 'border-amber-400 dark:border-amber-600'}`}
              >
                <div className="text-sm font-medium">{c.program_name}</div>
                <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-1 text-xs text-muted-foreground">
                  <div>
                    {t('members.duplicates.wizard.dupCase', {
                      id: c.enrollment_id,
                      status: c.status,
                      defaultValue: 'Duplicate case {{id}} — {{status}}',
                    })}
                    {' · '}
                    {caseCounts(c)}
                  </div>
                  <div>
                    {t('members.duplicates.wizard.keptCase', {
                      id: c.survivor_enrollment_id,
                      status: c.survivor_enrollment_status,
                      defaultValue: 'Kept record’s case {{id}} — {{status}}',
                    })}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-4 text-xs">
                  <label className="flex items-start gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      className="mt-0.5"
                      name={`case-${key}`}
                      checked={current === 'combine'}
                      onChange={() => setDecisions((d) => ({ ...d, [key]: 'combine' }))}
                    />
                    <span>
                      <span className="font-medium">{t('members.duplicates.wizard.combine', 'Combine')}</span>
                      {' — '}
                      {t(
                        'members.duplicates.wizard.combineHelp',
                        'notes, documents, activities and submitted forms join the kept record’s case; this case is closed (cancelled). Draft forms stay behind.'
                      )}
                    </span>
                  </label>
                  <label className="flex items-start gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      className="mt-0.5"
                      name={`case-${key}`}
                      checked={current === 'dont_merge'}
                      onChange={() => setDecisions((d) => ({ ...d, [key]: 'dont_merge' }))}
                    />
                    <span>
                      <span className="font-medium">
                        {t('members.duplicates.wizard.dontMerge', 'Don’t merge')}
                      </span>
                      {' — '}
                      {t(
                        'members.duplicates.wizard.dontMergeHelp',
                        'these are genuinely separate cases, so neither profile is merged: both stay active and the group is marked as different people.'
                      )}
                    </span>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderConfirm = () => {
    const chosen = Object.keys(fieldChoices);
    const decisionAction = (c: MergeCase): MergeCase['action'] => {
      const d = decisions[String(c.enrollment_id)];
      return d === 'combine' || d === 'dont_merge' ? d : null;
    };
    const byAction = (a: MergeCase['action']) =>
      (preview?.cases ?? []).filter((c) => (c.decision_required ? decisionAction(c) : c.action) === a);
    if (outcome === 'keep_both') {
      return (
        <div className="p-3 space-y-1.5 text-xs">
          <div className="font-medium">
            {t('members.duplicates.wizard.keepBothTitle', {
              count: byAction('dont_merge').length,
              defaultValue: '{{count}} case(s) marked “Don’t merge” — these profiles will not be merged.',
            })}
          </div>
          <div>{t('members.duplicates.wizard.oneCasePerProgram', ONE_CASE_PER_PROGRAM)}</div>
          <div className="flex items-start gap-1.5 pt-1 text-muted-foreground">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            {t(
              'members.duplicates.wizard.keepBothExplainer',
              'Both profiles stay active and visible, and this group is marked as different people so it stops coming back for review. You can undo that from the Merge history tab.'
            )}
          </div>
        </div>
      );
    }
    return (
      <div className="p-3 space-y-1.5 text-xs">
        <div>
          {t('members.duplicates.wizard.confirmKeep', {
            name: nameOf(survivorId),
            count: duplicateIds.length,
            defaultValue: 'Keep {{name}} and merge {{count}} duplicate record(s) into it.',
          })}
        </div>
        <div>
          {t('members.duplicates.wizard.confirmFields', {
            count: chosen.length,
            defaultValue: '{{count}} field(s) take a duplicate’s value by your choice.',
          })}
          {chosen.length > 0 &&
            `: ${chosen.map((k) => preview?.fields.find((f) => f.field === k)?.label ?? k).join(', ')}`}
        </div>
        <div>
          {t('members.duplicates.wizard.confirmFilled', {
            count: preview?.summary.filled_from_duplicate.length ?? 0,
            defaultValue: '{{count}} blank field(s) filled from a duplicate.',
          })}
        </div>
        <div>
          {t('members.duplicates.wizard.confirmCasesV2', {
            moved: byAction('move').length,
            combined: byAction('combine').length,
            defaultValue: 'Cases: {{moved}} moved, {{combined}} combined.',
          })}
        </div>
        <div className="flex items-start gap-1.5 pt-1 text-muted-foreground">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          {t(
            'members.duplicates.wizard.confirmRevert',
            'The duplicates are archived, not deleted. Every change is recorded, so this merge can be reverted from Merge history.'
          )}
        </div>
      </div>
    );
  };

  const steps: Step[] = ['fields', 'cases', 'confirm'];
  const index = steps.indexOf(step);

  return (
    <div className="mt-4 rounded-lg border">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2">
        <div className="flex flex-wrap items-center gap-4">
          <StepPill n={1} label={t('members.duplicates.wizard.stepKeep', 'Record to keep')} active={false} done />
          <StepPill
            n={2}
            label={t('members.duplicates.wizard.stepFields', 'Fields')}
            active={step === 'fields'}
            done={index > 0}
          />
          <StepPill
            n={3}
            label={t('members.duplicates.wizard.stepCases', 'Cases')}
            active={step === 'cases'}
            done={index > 1}
          />
          <StepPill
            n={4}
            label={t('members.duplicates.wizard.stepConfirm', 'Confirm')}
            active={step === 'confirm'}
            done={false}
          />
        </div>
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground"
          onClick={onCancel}
          aria-label={t('members.duplicates.wizard.cancel', 'Cancel merge')}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <Alert variant="destructive" className="m-3">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="flex items-center gap-2 p-4 text-xs text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('members.duplicates.wizard.loading', 'Working out what the merge would do…')}
        </div>
      )}

      {!loading && preview && (
        <>
          {step === 'fields' && renderFields()}
          {step === 'cases' && renderCases()}
          {step === 'confirm' && renderConfirm()}

          <div className="flex flex-wrap items-center justify-between gap-2 border-t px-3 py-2">
            <div className="text-xs text-muted-foreground">
              {caseState.undecided.length > 0 &&
                t('members.duplicates.wizard.undecidedCount', {
                  count: caseState.undecided.length,
                  defaultValue: '{{count}} case(s) still need a decision',
                })}
            </div>
            <div className="flex items-center gap-2">
              {index > 0 && (
                <Button
                  variant="ghost"
                  onClick={() => setStep(steps[index - 1])}
                  disabled={submitting}
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  {t('members.duplicates.wizard.back', 'Back')}
                </Button>
              )}
              {step !== 'confirm' ? (
                <Button
                  variant="secondary"
                  onClick={() => setStep(steps[index + 1])}
                  disabled={step === 'cases' && !mergeReady}
                >
                  {t('members.duplicates.wizard.next', 'Next')}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  variant="default"
                  disabled={!mergeReady || submitting}
                  onClick={() => void handleMerge()}
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-1" />
                  )}
                  {outcome === 'keep_both'
                    ? t('members.duplicates.wizard.keepBothButton', 'Keep both profiles')
                    : t('members.duplicates.wizard.mergeButton', 'Merge records')}
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MergeWizard;
